import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface AppReturnBannerProps {
  onDismiss?: () => void;
}

export const AppReturnBanner: React.FC<AppReturnBannerProps> = ({ onDismiss }) => {
  const { isLoggedIn, isAuthLoading, user } = useAuth();
  const location = useLocation();
  const [isDismissed, setIsDismissed] = React.useState(() => {
    return localStorage.getItem('appReturnBannerDismissed') === 'true';
  });

  // Don't show if not logged in, if dismissed, or if already in the app
  if (isAuthLoading || !isLoggedIn || isDismissed || location.pathname.startsWith('/app')) {
    return null;
  }

  const handleDismiss = () => {
    setIsDismissed(true);
    localStorage.setItem('appReturnBannerDismissed', 'true');
    onDismiss?.();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="fixed top-4 left-4 right-4 z-50 mx-auto max-w-md"
    >
      <div className="bg-gradient-to-r from-gold-400/95 to-gold-500/95 backdrop-blur-sm text-mindshift-900 rounded-2xl p-4 shadow-2xl border border-gold-300/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="bg-mindshift-900/20 rounded-full p-1.5">
              <Sparkles size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm">Welcome back, {user?.name || 'there'}!</p>
              <p className="text-xs opacity-90">Continue your transformation journey</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 ml-3">
            <Link
              to="/app/dashboard"
              className="inline-flex items-center gap-1 bg-mindshift-900/20 hover:bg-mindshift-900/30 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
            >
              Open App
              <ArrowRight size={12} />
            </Link>
            <button
              onClick={handleDismiss}
              className="p-1.5 hover:bg-mindshift-900/20 rounded-lg transition-colors"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};
